/**
 * Combo Product Picker - Zero Loading Time
 *
 * This component enables instant product switching without page reloads or loading screens.
 *
 * Features:
 * - Preloads all related products on page load for instant switching
 * - Updates entire product section dynamically
 * - Maintains browser history (back/forward buttons work)
 * - Updates URL without page reload
 * - Reinitializes all necessary components (Skio, Yotpo, media gallery, etc.)
 * - Updates meta tags and SEO elements
 * - Fallback to full page load if preload fails
 *
 * @class ComboProductPicker
 * @extends HTMLElement
 */

class ComboProductPicker extends HTMLElement {
  constructor() {
    super();
    this.cache = new Map();
    this.currentProductUrl = this.normalizeUrl(window.location.pathname);
    this.boundOnPopState = this.onPopState.bind(this);
    this.boundOnChange = this.onProductChange.bind(this);
  }

  connectedCallback() {
    this.sectionId = this.dataset.section || this.findSectionId();
    if (!this.sectionId) return;

    this.preloadRelatedProducts();
    this.addEventListener('change', this.boundOnChange);
    window.addEventListener('popstate', this.boundOnPopState);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.boundOnPopState);
    this.removeEventListener('change', this.boundOnChange);
  }

  normalizeUrl(url) {
    if (!url) return '';
    const cleanUrl = url.split('?')[0].split('#')[0];
    return cleanUrl === '/' ? cleanUrl : cleanUrl.replace(/\/$/, '');
  }

  findSectionId() {
    const productSection = this.closest('[id^="shopify-section-"]');
    if (productSection) {
      const match = productSection.id.match(/shopify-section-(.*)/);
      if (match) return match[1];
    }

    const mainProductSection = document.querySelector('[id*="shopify-section-template"], [id*="main-product"]');
    if (mainProductSection) {
      const match = mainProductSection.id.match(/shopify-section-(.*)/);
      return match ? match[1] : 'main';
    }

    return 'main';
  }

  preloadRelatedProducts() {
    const radios = this.querySelectorAll('input[type="radio"]');
    const preloadPromises = [];

    radios.forEach((radio) => {
      const productUrl = radio.dataset.productUrl;
      if (!productUrl) return;

      const normalizedUrl = this.normalizeUrl(productUrl);
      if (normalizedUrl && normalizedUrl !== this.currentProductUrl) {
        preloadPromises.push(this.fetchAndCacheProduct(normalizedUrl));
      }
    });

    this.cacheCurrentProduct();

    if (preloadPromises.length > 0) {
      Promise.all(preloadPromises);
    }
  }

  cacheCurrentProduct() {
    const sectionElement = document.querySelector(`#shopify-section-${this.sectionId}`);
    const currentHtml = sectionElement ? sectionElement.outerHTML : document.documentElement.outerHTML;
    this.cache.set(this.currentProductUrl, currentHtml);
  }

  async fetchAndCacheProduct(url) {
    const normalizedUrl = this.normalizeUrl(url);
    if (!normalizedUrl || this.cache.has(normalizedUrl)) {
      return this.cache.get(normalizedUrl) || null;
    }

    try {
      const response = await fetch(`${normalizedUrl}?section_id=${this.sectionId}`, {
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const html = await response.text();
      this.cache.set(normalizedUrl, html);
      return html;
    } catch (error) {
      return null;
    }
  }

  onProductChange(event) {
    if (event.target.type !== 'radio') return;

    const productUrl = event.target.dataset.productUrl;
    const productHandle = event.target.dataset.productHandle;

    if (!productUrl) return;

    const normalizedUrl = this.normalizeUrl(productUrl);

    if (normalizedUrl === this.currentProductUrl) {
      event.target.checked = true;
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.switchProduct(normalizedUrl, productHandle);
  }

  switchProduct(url, handle) {
    const normalizedUrl = this.normalizeUrl(url);
    const cachedHtml = this.cache.get(normalizedUrl);

    if (cachedHtml) {
      try {
        this.updateDOM(cachedHtml, normalizedUrl);
        this.updateURL(normalizedUrl);
        this.currentProductUrl = normalizedUrl;
        this.dispatchProductChangeEvents(handle);
      } catch (error) {
        window.location.href = normalizedUrl;
      }
    } else {
      this.fetchAndSwitchProduct(normalizedUrl, handle);
    }
  }

  async fetchAndSwitchProduct(url, handle) {
    this.classList.add('loading');

    try {
      const html = await this.fetchAndCacheProduct(url);
      if (html) {
        this.updateDOM(html, url);
        this.updateURL(url);
        this.currentProductUrl = this.normalizeUrl(url);
        this.dispatchProductChangeEvents(handle);
      } else {
        window.location.href = url;
      }
    } catch (error) {
      window.location.href = url;
    } finally {
      this.classList.remove('loading');
    }
  }

  updateDOM(html, productUrl) {
    const parser = new DOMParser();
    const newDoc = parser.parseFromString(html, 'text/html');

    if (newDoc.querySelector('parsererror')) {
      throw new Error('Failed to parse HTML response');
    }

    // Update product content areas while preserving the picker
    this.updateProductContent(newDoc);

    // Update meta tags
    this.updateMetaTags(newDoc, productUrl);

    // Reinitialize components
    requestAnimationFrame(() => {
      this.reinitializeComponents();
    });

    this.scrollToProduct();
  }

  updateProductContent(newDoc) {
    // Update media gallery
    const currentGallery = document.querySelector(`#MediaGallery-${this.sectionId}`);
    const newGallery = newDoc.querySelector(`#MediaGallery-${this.sectionId}`);
    if (currentGallery && newGallery) {
      this.replaceMediaGallery(currentGallery, newGallery);
    }

    // Update card badge (product badge on image)
    const currentBadge = document.querySelector('.card__badge');
    const newBadge = newDoc.querySelector('.card__badge');
    if (currentBadge && newBadge) {
      currentBadge.innerHTML = newBadge.innerHTML;
      Array.from(newBadge.attributes).forEach((attr) => {
        currentBadge.setAttribute(attr.name, attr.value);
      });
    }

    // Update product info wrapper (contains title, price, form, accordions, etc.)
    const currentInfoWrapper = document.querySelector(`#ProductInfo-${this.sectionId}`);
    const newInfoWrapper = newDoc.querySelector(`#ProductInfo-${this.sectionId}`);
    if (currentInfoWrapper && newInfoWrapper) {
      this.updateInfoWrapperElements(currentInfoWrapper, newInfoWrapper);
      this.updateAllInfoElements(currentInfoWrapper, newInfoWrapper);
    }

    // Update product modal
    const currentModal = document.querySelector(`#ProductModal-${this.sectionId}`);
    const newModal = newDoc.querySelector(`#ProductModal-${this.sectionId}`);
    if (currentModal && newModal) {
      currentModal.innerHTML = newModal.innerHTML;
    }

    // Update Yotpo widgets throughout the page with new product ID
    this.updateYotpoWidgets(newDoc);
  }

  updateYotpoWidgets(newDoc) {
    // Get the new product ID from the new document
    const newYotpoWidget = newDoc.querySelector('.yotpo-widget-instance[data-yotpo-product-id]');
    const newProductId = newYotpoWidget?.getAttribute('data-yotpo-product-id');
    const newProductName = newYotpoWidget?.getAttribute('data-yotpo-name');

    if (!newProductId) return;

    // Find all Yotpo widgets on the current page (including those at the bottom)
    const allYotpoWidgets = document.querySelectorAll('.yotpo-widget-instance[data-yotpo-product-id]');

    // Update each widget with the new product ID
    allYotpoWidgets.forEach((widget) => {
      widget.setAttribute('data-yotpo-product-id', newProductId);
      if (newProductName) {
        widget.setAttribute('data-yotpo-name', newProductName);
      }
    });
  }

  replaceElement(currentEl, newEl) {
    const parent = currentEl.parentNode;
    const nextSibling = currentEl.nextSibling;
    currentEl.remove();
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = newEl.outerHTML;
    const newElement = tempDiv.firstElementChild;
    if (nextSibling) {
      parent.insertBefore(newElement, nextSibling);
    } else {
      parent.appendChild(newElement);
    }
    return newElement;
  }

  updateAccordions(currentWrapper, newWrapper) {
    const currentElements = Array.from(currentWrapper.querySelectorAll('accordion-tab'));
    const newElements = Array.from(newWrapper.querySelectorAll('accordion-tab'));

    if (currentElements.length === 0 && newElements.length === 0) return;

    const container =
      currentElements.length > 0
        ? currentElements[0].parentNode
        : currentWrapper.querySelector('.product__info-container') || currentWrapper;

    const insertReference = currentElements.length > 0 ? currentElements[currentElements.length - 1].nextSibling : null;

    currentElements.forEach((el) => el.remove());

    newElements.forEach((newEl) => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = newEl.outerHTML;
      const newElement = tempDiv.firstElementChild;
      if (insertReference && insertReference.parentNode === container) {
        container.insertBefore(newElement, insertReference);
      } else {
        container.appendChild(newElement);
      }
    });
  }

  updateInfoWrapperElements(currentWrapper, newWrapper) {
    const elementsToUpdate = [
      { selector: '.product__title', updateMethod: 'innerHTML' },
      { selector: '.product__heading', updateMethod: 'innerHTML' },
      { selector: '.product__subtitle', updateMethod: 'innerHTML' },
      { selector: '.product__text.subtitle', updateMethod: 'innerHTML' },
      { selector: `#price-${this.sectionId}`, updateMethod: 'innerHTML' },
      { selector: `#price-${this.sectionId}--alt`, updateMethod: 'innerHTML' },
      { selector: '.price', updateMethod: 'innerHTML' },
      { selector: `#sku-${this.sectionId}`, updateMethod: 'innerHTML' },
      { selector: '.product-info-icons', updateMethod: 'innerHTML' },
      { selector: '.product--info', updateMethod: 'innerHTML' },
      { selector: '.card__badge', updateMethod: 'innerHTML' },
      { selector: '[id*="shopify-block"][id*="yotpo"]', updateMethod: 'replace' },
      { selector: '.yotpo-widget-instance', updateMethod: 'replace' },
      { selector: '.custom__liquid', updateMethod: 'innerHTML' },
      { selector: '#skio-widget', updateMethod: 'replace' },
      { selector: 'skio-plan-picker', updateMethod: 'replace' },
      { selector: 'skio-plan-picker-multi', updateMethod: 'replace' },
      { selector: '.pdp-msg', updateMethod: 'innerHTML' },
      { selector: `#product-form-${this.sectionId}`, updateMethod: 'form' },
      { selector: 'product-form', updateMethod: 'replace' },
      { selector: 'pickup-availability', updateMethod: 'replace' },
      {
        selector: `variant-radios[data-section="${this.sectionId}"], variant-selects[data-section="${this.sectionId}"]`,
        updateMethod: 'replace',
      },
    ];

    elementsToUpdate.forEach(({ selector, updateMethod }) => {
      const currentEl = currentWrapper.querySelector(selector);
      const newEl = newWrapper.querySelector(selector);

      if (!currentEl || !newEl) return;

      if (updateMethod === 'innerHTML') {
        currentEl.innerHTML = newEl.innerHTML;
      } else if (updateMethod === 'form') {
        const currentInput = currentEl.querySelector('input[name="id"]');
        const newInput = newEl.querySelector('input[name="id"]');
        if (currentInput && newInput) currentInput.value = newInput.value;
        currentEl.action = newEl.action;

        newEl.querySelectorAll('input[type="hidden"]').forEach((newInput) => {
          const name = newInput.name;
          const currentInput = currentEl.querySelector(`input[name="${name}"]`);
          if (currentInput) {
            currentInput.value = newInput.value;
          } else {
            currentEl.appendChild(newInput.cloneNode(true));
          }
        });
      } else if (updateMethod === 'replace') {
        const newElement = this.replaceElement(currentEl, newEl);

        // Yotpo widgets will be reinitialized in reinitializeComponents()
        // No need to initialize here to avoid double initialization

        if (newElement.id === 'skio-widget' || newElement.querySelector('#skio-widget')) {
          this.removeDuplicateSkioWidgets(newElement);
        }
      }
    });

    this.updateAccordions(currentWrapper, newWrapper);
  }

  removeDuplicateSkioWidgets(element) {
    const container = element.id === 'skio-widget' ? element.parentNode : element;
    if (!container) return;

    // Remove duplicate #skio-widget elements
    const allSkioWidgets = container.querySelectorAll('#skio-widget');
    if (allSkioWidgets.length > 1) {
      for (let i = 1; i < allSkioWidgets.length; i++) {
        allSkioWidgets[i].remove();
      }
    }

    // Remove duplicate skio-plan-picker custom elements
    const allSkioPickers = container.querySelectorAll('skio-plan-picker');
    if (allSkioPickers.length > 1) {
      for (let i = 1; i < allSkioPickers.length; i++) {
        allSkioPickers[i].remove();
      }
    }

    // Remove duplicate skio-plan-picker-multi custom elements
    const allSkioPickersMulti = container.querySelectorAll('skio-plan-picker-multi');
    if (allSkioPickersMulti.length > 1) {
      for (let i = 1; i < allSkioPickersMulti.length; i++) {
        allSkioPickersMulti[i].remove();
      }
    }
  }

  updateAllInfoElements(currentWrapper, newWrapper) {
    const currentChildren = Array.from(currentWrapper.children);
    const newChildren = Array.from(newWrapper.children);
    const updatedElements = new Set();

    currentChildren.forEach((currentChild, index) => {
      if (
        currentChild === this ||
        currentChild.querySelector('combo-product-picker') ||
        updatedElements.has(currentChild) ||
        currentChild.id === 'skio-widget' ||
        currentChild.querySelector('#skio-widget') ||
        currentChild.tagName === 'SKIO-PLAN-PICKER' ||
        currentChild.tagName === 'SKIO-PLAN-PICKER-MULTI' ||
        currentChild.querySelector('skio-plan-picker, skio-plan-picker-multi') ||
        currentChild.tagName === 'ACCORDION-TAB' ||
        currentChild.querySelector('accordion-tab')
      ) {
        return;
      }

      let matchingNewChild = currentChild.id
        ? newWrapper.querySelector(`#${currentChild.id}`)
        : newChildren[index] && newChildren[index] !== this
        ? newChildren[index]
        : null;

      if (!matchingNewChild && currentChild.className) {
        const firstClass = currentChild.className.split(' ')[0];
        if (firstClass) {
          const matches = newWrapper.querySelectorAll(`.${firstClass}`);
          if (matches.length === 1) matchingNewChild = matches[0];
        }
      }

      if (matchingNewChild && matchingNewChild !== this && currentChild.tagName === matchingNewChild.tagName) {
        if (currentChild.tagName.includes('-')) {
          this.replaceElement(currentChild, matchingNewChild);
          updatedElements.add(matchingNewChild);
        } else {
          currentChild.innerHTML = matchingNewChild.innerHTML;
          Array.from(matchingNewChild.attributes).forEach((attr) => {
            currentChild.setAttribute(attr.name, attr.value);
          });
          updatedElements.add(currentChild);
        }
      }
    });

    // Clean up duplicate Skio widgets
    this.removeDuplicateSkioWidgets(currentWrapper);
  }

  replaceMediaGallery(currentGallery, newGallery) {
    // Destroy Flickity instances
    const flickityInstances = currentGallery.querySelectorAll('.flickity-enabled');
    flickityInstances.forEach((instance) => {
      if (window.Flickity) {
        const flkty = window.Flickity.data(instance);
        if (flkty) flkty.destroy();
      }
    });

    // Store parent and position
    const parent = currentGallery.parentNode;
    const nextSibling = currentGallery.nextSibling;

    // Remove old gallery
    currentGallery.remove();

    // Create new gallery element
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = newGallery.outerHTML;
    const newGalleryElement = tempDiv.firstElementChild;

    // Insert new gallery
    if (nextSibling) {
      parent.insertBefore(newGalleryElement, nextSibling);
    } else {
      parent.appendChild(newGalleryElement);
    }
  }

  updateOrCreateMeta(selector, attribute, value, createFn) {
    let current = document.querySelector(selector);
    if (current) {
      current.setAttribute(attribute, value);
    } else {
      current = createFn();
      current.setAttribute(attribute, value);
      document.head.appendChild(current);
    }
  }

  updateMetaTags(newDoc, productUrl) {
    try {
      const newTitle = newDoc.querySelector('title');
      if (newTitle) document.title = newTitle.textContent;

      const newMetaDesc = newDoc.querySelector('meta[name="description"]');
      if (newMetaDesc) {
        this.updateOrCreateMeta(
          'meta[name="description"]',
          'content',
          newMetaDesc.getAttribute('content') || '',
          () => {
            const meta = document.createElement('meta');
            meta.setAttribute('name', 'description');
            return meta;
          }
        );
      }

      ['og:title', 'og:description', 'og:image', 'og:url'].forEach((tag) => {
        const newTag = newDoc.querySelector(`meta[property="${tag}"]`);
        if (newTag) {
          this.updateOrCreateMeta(`meta[property="${tag}"]`, 'content', newTag.getAttribute('content') || '', () => {
            const meta = document.createElement('meta');
            meta.setAttribute('property', tag);
            return meta;
          });
        }
      });

      const newCanonical = newDoc.querySelector('link[rel="canonical"]');
      if (newCanonical) {
        const href = newCanonical.getAttribute('href') || productUrl;
        this.updateOrCreateMeta('link[rel="canonical"]', 'href', href, () => {
          const link = document.createElement('link');
          link.setAttribute('rel', 'canonical');
          return link;
        });
      }
    } catch (error) {
      // Silently fail meta tag updates
    }
  }

  updateURL(url) {
    const state = { productUrl: url, timestamp: Date.now() };
    history.pushState(state, '', url);
  }

  onPopState() {
    const targetUrl = this.normalizeUrl(window.location.pathname);

    if (targetUrl === this.currentProductUrl) return;

    const radios = this.querySelectorAll('input[type="radio"]');
    let targetRadio = null;

    for (const radio of radios) {
      const radioUrl = this.normalizeUrl(radio.dataset.productUrl);
      if (radioUrl === targetUrl) {
        targetRadio = radio;
        break;
      }
    }

    if (targetRadio) {
      targetRadio.checked = true;
      this.switchProduct(targetUrl, targetRadio.dataset.productHandle);
    } else {
      window.location.reload();
    }
  }

  reinitializeComponents() {
    try {
      // Reinitialize media gallery - force custom element to reinitialize
      const mediaGallery = document.querySelector(`#MediaGallery-${this.sectionId}`);
      if (mediaGallery) {
        // Clone and replace to trigger connectedCallback which sets up thumbnail click handlers
        const parent = mediaGallery.parentNode;
        const nextSibling = mediaGallery.nextSibling;
        const clone = mediaGallery.cloneNode(true);

        // Remove old instance
        mediaGallery.remove();

        // Insert clone - this will trigger connectedCallback in media-gallery.js
        if (nextSibling) {
          parent.insertBefore(clone, nextSibling);
        } else {
          parent.appendChild(clone);
        }
      }

      // Reinitialize other components
      setTimeout(() => {
        // Yotpo - initialize widgets once after product change
        if (window.yotpoWidgetsContainer?.initWidgets) {
          window.yotpoWidgetsContainer.initWidgets();
        } else if (window.yotpo?.refreshWidgets) {
          window.yotpo.refreshWidgets();
        }

        // Skio - Force reinitialize by waiting for custom element to connect
        // Then dispatch variant changed event
        const skioWidget = document.querySelector('skio-plan-picker, skio-plan-picker-multi');
        const variantId = document.querySelector(`#product-form-${this.sectionId} input[name="id"]`)?.value;

        if (skioWidget && variantId) {
          // Wait for Skio custom element to fully initialize
          setTimeout(() => {
            document.dispatchEvent(
              new CustomEvent('variantChanged', {
                detail: { variantId: parseInt(variantId, 10) },
                bubbles: true,
              })
            );
          }, 100);
        } else if (variantId) {
          // Fallback if no Skio widget found
          document.dispatchEvent(
            new CustomEvent('variantChanged', {
              detail: { variantId: parseInt(variantId, 10) },
              bubbles: true,
            })
          );
        }

        // Variant picker
        const variantPicker = document.querySelector(
          `variant-radios[data-section="${this.sectionId}"], variant-selects[data-section="${this.sectionId}"]`
        );
        if (variantPicker?.updateOptions) {
          variantPicker.updateOptions();
        }

        // Product form
        const productForm = document.querySelector(`product-form[data-section="${this.sectionId}"]`);
        if (productForm) {
          productForm.dispatchEvent(new CustomEvent('product:loaded', { bubbles: true }));
        }

        // Pickup availability
        const pickupAvailability = document.querySelector('pickup-availability');
        const variantIdForPickup = document.querySelector(`#product-form-${this.sectionId} input[name="id"]`)?.value;
        if (pickupAvailability?.fetchAvailability && variantIdForPickup) {
          pickupAvailability.fetchAvailability(variantIdForPickup);
        }

        // Global product info loaded event
        document.dispatchEvent(
          new CustomEvent('productInfo:loaded', {
            detail: { sectionId: this.sectionId },
            bubbles: true,
          })
        );
      }, 200);
    } catch (error) {
      // Silently fail component reinitialization
    }
  }

  dispatchProductChangeEvents(handle) {
    document.dispatchEvent(
      new CustomEvent('combo-product:changed', {
        detail: {
          productHandle: handle,
          productUrl: this.currentProductUrl,
          timestamp: Date.now(),
        },
      })
    );

    const recentlyViewed = document.querySelector('product-recently-viewed');
    if (recentlyViewed) {
      const productId = recentlyViewed.dataset.productId;
      if (productId) {
        document.dispatchEvent(
          new CustomEvent('product:viewed', {
            detail: { productId: parseInt(productId) },
          })
        );
      }
    }

    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'combo_product_switch',
        productHandle: handle,
        productUrl: this.currentProductUrl,
      });
    }
  }

  scrollToProduct() {
    const productSection = document.querySelector('#anchorlink');
    if (!productSection) return;

    const offset = 100;
    const elementPosition = productSection.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }
}

customElements.define('combo-product-picker', ComboProductPicker);
